<template>
  <div class="q-my-md">
    <div
      class="text-center text-h5 q-pa-md"
      v-if="currentUnitId"
    >
      {{ unitDetails.complexName }}: Update Unit
    </div>
    <div
      class="text-center text-h5 q-pa-md"
      v-else
    >
      {{ unitDetails.complexName }}: Add New Unit
    </div>
    <div
      class="flex flex-center"
      padding
    >
      <q-form
        ref="validateForm"
        @submit="onSubmit"
        style="width: 600px"
        greedy
      >
        <div class="row">
          <q-input
            v-model="unitDetails.name"
            class="col-12"
            label="Unit Name"
            :rules="[checkUnitNameDuplicates, checkIsValidUnitName, checkInput]"
            lazy-rules
            debounce="1000"
            @update:model-value="hasRoomsInColleague(unitDetails.name, complexDetails.complexCode)"
            :borderless="isRa"
            :readonly="isRa"
          />
        </div>
        <div class="row">
          <q-select
            v-model="unitDetails.roomRateClass"
            class="col-12"
            label="Room Rate Class"
            :options="roomRateClasses"
            behavior="menu"
            :rules="[checkSelect]"
            lazy-rules
            :borderless="isRa"
            :readonly="isRa"
            :hide-dropdown-icon="isRa"
          />
        </div>
        <div>
          <div class="row items-start">
            <q-input
              class="col-4"
              label="Master Bedroom:"
              readonly
              borderless
            />
            <q-input
              v-model.number="masterCapacity"
              class="col-2 q-mr-sm"
              label="Capacity"
              :rules="[checkInput, checkIsValidNumber]"
              lazy-rules
              dense
              :borderless="isRa"
              :readonly="isRa"
            />
            <q-input
              v-model="roomDescriptionList[0]"
              class="col-4 q-mr-sm"
              label="Description"
              :rules="[checkIsValidDescription]"
              lazy-rules
              dense
              :borderless="isRa"
              :readonly="isRa"
            />
            <q-icon
              name="sync_alt"
              :color="hasRoomsInColleagueList[0] ? 'green' : 'grey'"
              class="q-mt-md q-mr-sm"
              size="sm"
            >
              <q-tooltip>
                Exists in Colleague when green.
              </q-tooltip>
            </q-icon>
          </div>
          <div
            v-if="hasNonMasterBedroom"
            class="row items-start"
          >
            <q-input
              class="col-4"
              label="Non Master Bedroom:"
              readonly
              borderless
            />
            <q-input
              v-model.number="nonMasterCapacity"
              class="col-2 q-mr-sm"
              label="Capacity"
              :rules="[checkInput, checkIsValidNumber]"
              lazy-rules
              dense
              :borderless="isRa"
              :readonly="isRa"
            />
            <q-input
              v-model="roomDescriptionList[1]"
              class="col-4 q-mr-sm"
              label="Description"
              :rules="[checkIsValidDescription]"
              lazy-rules
              dense
              :borderless="isRa"
              :readonly="isRa"
            />
            <q-icon
              name="sync_alt"
              :color="hasRoomsInColleagueList[1] ? 'green' : 'grey'"
              :background-color="hasRoomsInColleagueList[1] ? 'green' : 'grey'"
              class="q-mt-md q-mr-sm"
              size="sm"
            >
              <q-tooltip>
                Exists in Colleague when green.
              </q-tooltip>
            </q-icon>
            <q-btn
              v-if="!hasSingleBedroom && !isRa"
              @click="removeBedroom"
              class="q-mt-md"
              icon="remove"
              color="primary"
              size="xs"
              round
            />
          </div>
          <div
            v-if="hasSingleBedroom"
            class="row items-start"
          >
            <q-input
              class="col-4"
              label="Single Bedroom:"
              readonly
              borderless
            />
            <q-input
              v-model.number="singleCapacity"
              class="col-2 q-mr-sm"
              label="Capacity"
              :rules="[checkInput, checkIsValidNumber]"
              lazy-rules
              dense
              :borderless="isRa"
              :readonly="isRa"
            />
            <q-input
              v-model="roomDescriptionList[2]"
              class="col-4 q-mr-sm"
              label="Description"
              :rules="[checkIsValidDescription]"
              lazy-rules
              dense
              :borderless="isRa"
              :readonly="isRa"
            />
            <q-icon
              name="sync_alt"
              :color="hasRoomsInColleagueList[2] ? 'green' : 'grey'"
              class="q-mt-md q-mr-sm"
              size="sm"
            >
              <q-tooltip>
                Exists in Colleague when green.
              </q-tooltip>
            </q-icon>
            <q-btn
              v-if="!isRa"
              class="q-mt-md"
              @click="removeBedroom"
              icon="remove"
              color="primary"
              size="xs"
              round
            />
          </div>

          <div
            v-if="!hasSingleBedroom && !isRa"
            class="float-right q-mt-md"
          >
            <q-btn
              @click="addBedroom"
              icon="add"
              color="primary"
              size="xs"
              round
            />
          </div>
        </div>
        <q-checkbox
          class="full-width align-left"
          v-model="unitDetails.dssAccessible"
          label="DSS Accessible"
          :disable="isRa"
        />
        <div class="q-my-md">
          <q-input
            v-model="unitDetails.doorCode"
            label="Door/Key Code"
          />
          <q-input
            v-model="unitDetails.wiFiName"
            label="WiFi Name"
          />
          <q-input
            v-model="unitDetails.wiFiPassword"
            label="WiFi Password"
          />
          <q-input
            v-model="unitDetails.parking"
            label="Parking"
          />

          <q-input
            class="q-mt-md"
            filled
            v-model="unitDetails.notes"
            label="Notes"
            type="textarea"
          />
        </div>
        <div v-if="isShadowbrookComplex(complexDetails.complexCode)">
          <q-input
            v-model="unitDetails.mailboxNumber"
            label="Mailbox Number"
          />
        </div>
      </q-form>
    </div>
  </div>
</template>

<script>
import { ref, onUnmounted } from 'vue';
import { useQuasar } from 'quasar';
import useAuth from '@/hooks/auth';
import useUnit from '@/hooks/unit';
import useComplex from '@/hooks/complex';
import useUiUtilities from '@/utilities/uiUtilities';
import useUiValidations from '@/utilities/uiValidations';
import useUtility from '@/hooks/utility';

export default {
  async setup() {
    const $q = useQuasar();
    const { hasRoles } = useAuth();
    const {
      unitDetails, createUnit, fetchUnit, updateUnit, updateUnitWifiAndParking,
      currentUnitId, fetchUnitNames,
    } = useUnit();

    const {
      checkInput,
      checkSelect,
      checkIsValidNumber,
      checkIsValidDescription,
      checkIsValidUnitName,
    } = useUiValidations();

    const {
      currentComplexId,
      resetCurrentComplexId,
      fetchComplexName,
      complexName,
      complexDetails,
    } = useComplex();
    const {
      notifyResult,
      isShadowbrookComplex,
    } = useUiUtilities();
    const {
      roomRateClasses,
      getRoomRateClasses,
      hasRoomsInColleague,
      hasRoomsInColleagueList,
    } = useUtility();

    onUnmounted(() => {
      resetCurrentComplexId();
    });

    const validateForm = ref(null);
    const hasNonMasterBedroom = ref(false);
    const hasSingleBedroom = ref(false);
    const masterCapacity = ref();
    const nonMasterCapacity = ref();
    const singleCapacity = ref();
    const roomDescriptionList = ref(['', '', '']);
    const isRa = !hasRoles('HRL', 'R&D') && hasRoles('RESIDENT-ASSISTANT');

    const populateUnitRooms = () => {
      unitDetails.value.roomModels[0] = {
        roomName: `${unitDetails.value.name}M`,
        capacity: masterCapacity.value,
        roomType: 'Master',
        description: roomDescriptionList.value[0],
      };

      if (hasNonMasterBedroom.value) {
        unitDetails.value.roomModels[1] = {
          roomName: `${unitDetails.value.name}NM`,
          capacity: nonMasterCapacity.value,
          roomType: 'NonMaster',
          description: roomDescriptionList.value[1],
        };
      } else {
        unitDetails.value.roomModels.splice(1, 2);
      }

      if (hasSingleBedroom.value) {
        unitDetails.value.roomModels[2] = {
          roomName: unitDetails.value.name,
          capacity: singleCapacity.value,
          roomType: 'Single',
          description: roomDescriptionList.value[2],
        };
      } else {
        unitDetails.value.roomModels.splice(2, 1);
      }
    };

    const onSubmit = async () => {
      const validationResult = await validateForm.value.validate();
      if (validationResult) {
        $q.loading.show();
        populateUnitRooms();
        let response = '';
        if (currentUnitId.value === undefined || currentUnitId.value === null) {
          response = await createUnit();
          if (response.status === 201 || response.status === 200) {
            unitDetails.value = response.data.data;
            currentUnitId.value = response.data.data.unitId;
          }
        } else if (isRa) {
          response = await updateUnitWifiAndParking(currentUnitId.value);
        } else {
          response = await updateUnit(currentUnitId.value);
        }
        notifyResult(response.status, response);
        $q.loading.hide();

        return response.status;
      }

      return 400;
    };

    if (currentUnitId.value) {
      const fetchedUnit = await fetchUnit(currentUnitId.value);
      if (fetchedUnit.status) {
        notifyResult(fetchedUnit.status, fetchedUnit);
      }
      currentComplexId.value = unitDetails.value.complexId;
      masterCapacity.value = unitDetails.value.roomModels[0]?.capacity;
      nonMasterCapacity.value = unitDetails.value.roomModels[1]?.capacity;
      singleCapacity.value = unitDetails.value.roomModels[2]?.capacity;
      roomDescriptionList.value[0] = unitDetails.value.roomModels[0]?.description;
      roomDescriptionList.value[1] = unitDetails.value.roomModels[1]?.description;
      roomDescriptionList.value[2] = unitDetails.value.roomModels[2]?.description;
      if (nonMasterCapacity.value) {
        hasNonMasterBedroom.value = true;
      }
      if (singleCapacity.value) {
        hasSingleBedroom.value = true;
      }
    }

    const populateComplexInfo = async () => {
      try {
        if (currentComplexId) {
          unitDetails.value.complexId = currentComplexId.value;
          await fetchComplexName(currentComplexId.value);
          unitDetails.value.complexName = complexName.value;
        } else {
          unitDetails.value.complexId = '';
        }
      } catch (error) {
        console.log(error);
      }
    };

    const unitNames = await fetchUnitNames(currentComplexId.value);
    const initialUnitName = unitDetails.value.name;
    const checkUnitNameDuplicates = (val) => {
      if (val === initialUnitName) {
        return true;
      }
      for (let i = 0; i < unitNames.length; i += 1) {
        if (val.trim() === unitNames[i].unitName.trim()) {
          return 'Cannot use existing unit name.';
        }
      }

      return true;
    };

    const addBedroom = () => {
      if (hasNonMasterBedroom.value) {
        hasSingleBedroom.value = true;
      } else {
        hasNonMasterBedroom.value = true;
      }
    };

    const removeBedroom = () => {
      if (hasSingleBedroom.value) {
        singleCapacity.value = '';
        hasSingleBedroom.value = false;
      } else {
        nonMasterCapacity.value = '';
        hasNonMasterBedroom.value = false;
      }
    };

    await populateComplexInfo();
    await getRoomRateClasses();
    await hasRoomsInColleague(unitDetails.value.name, complexDetails.value.complexCode);

    return {
      addBedroom,
      checkInput,
      checkIsValidNumber,
      checkIsValidDescription,
      checkIsValidUnitName,
      checkSelect,
      checkUnitNameDuplicates,
      complexDetails,
      currentUnitId,
      hasRoomsInColleague,
      hasNonMasterBedroom,
      hasSingleBedroom,
      hasRoomsInColleagueList,
      isRa,
      masterCapacity,
      nonMasterCapacity,
      onSubmit,
      removeBedroom,
      roomDescriptionList,
      roomRateClasses,
      singleCapacity,
      unitDetails,
      unitTypeOptions: [
        'Master', 'NonMaster',
      ],
      validateForm,
      isShadowbrookComplex,
    };
  },
};
</script>
